/**
 * Calibration Procedure Form Modal Component
 * Create/Edit calibration procedure form in a dialog
 */

import { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Loader2 } from 'lucide-react';
import { useCalibratorsList } from '@/features/calibrators';
import { useCreateCalibrationProcedure, useUpdateCalibrationProcedure } from '../hooks';
import { MeasurementTemplateEditor } from './MeasurementTemplateEditor';
import type { CalibrationProcedure, MeasurementTemplate } from '../types';
import { PROCEDURE_TAGS } from '../types';

interface CalibrationProcedureModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  procedure?: CalibrationProcedure;
}

export function CalibrationProcedureModal({
  open,
  onOpenChange,
  procedure,
}: CalibrationProcedureModalProps) {
  const isEditing = !!procedure;

  // ---------------------------------------------------------------------------
  // Form state (controlled useState pattern)
  // ---------------------------------------------------------------------------
  const [name, setName] = useState('');
  const [dueDateMonths, setDueDateMonths] = useState(12);
  const [selectedCalibratorIds, setSelectedCalibratorIds] = useState<string[]>([]);
  const [tags, setTags] = useState<string[]>([]);
  const [measurementTemplates, setMeasurementTemplates] = useState<MeasurementTemplate[]>([]);
  const [description, setDescription] = useState('');

  // ---------------------------------------------------------------------------
  // Load calibrators for the multi-select
  // ---------------------------------------------------------------------------
  const { data: calibratorsData } = useCalibratorsList({ pageLength: 100 });
  const calibrators = calibratorsData?.calibrators || [];

  // ---------------------------------------------------------------------------
  // Mutations
  // ---------------------------------------------------------------------------
  const createMutation = useCreateCalibrationProcedure();
  const updateMutation = useUpdateCalibrationProcedure();
  const isPending = createMutation.isPending || updateMutation.isPending;

  // ---------------------------------------------------------------------------
  // Reset form when modal opens/closes or procedure changes
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (open && procedure) {
      setName(procedure.name);
      setDueDateMonths(procedure.dueDateMonths);
      setSelectedCalibratorIds(procedure.calibratorIds || []);
      setTags(procedure.tags || []);
      setMeasurementTemplates(procedure.measurementTemplates || []);
      setDescription(procedure.description || '');
    } else if (open && !procedure) {
      setName('');
      setDueDateMonths(12);
      setSelectedCalibratorIds([]);
      setTags([]);
      setMeasurementTemplates([]);
      setDescription('');
    }
  }, [open, procedure]);

  // ---------------------------------------------------------------------------
  // Tag toggle handler
  // ---------------------------------------------------------------------------
  const handleTagToggle = (tag: string, checked: boolean) => {
    if (checked) {
      setTags([...tags, tag]);
    } else {
      setTags(tags.filter((t) => t !== tag));
    }
  };

  // ---------------------------------------------------------------------------
  // Calibrator toggle handler
  // ---------------------------------------------------------------------------
  const handleCalibratorToggle = (mongoCalibratorId: string, checked: boolean) => {
    if (checked) {
      setSelectedCalibratorIds([...selectedCalibratorIds, mongoCalibratorId]);
    } else {
      setSelectedCalibratorIds(selectedCalibratorIds.filter((id) => id !== mongoCalibratorId));
    }
  };

  // ---------------------------------------------------------------------------
  // Submit handler
  // ---------------------------------------------------------------------------
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const payload = {
      name,
      due_date: dueDateMonths,
      calibrators: selectedCalibratorIds.length > 0 ? selectedCalibratorIds : undefined,
      tags: tags.length > 0 ? tags : undefined,
      measurement_templates: measurementTemplates.length > 0 ? measurementTemplates : undefined,
      description: description || undefined,
    };

    if (isEditing) {
      await updateMutation.mutateAsync({
        id: procedure.mongoProcedureId,
        data: payload,
      });
    } else {
      await createMutation.mutateAsync(payload);
    }

    onOpenChange(false);
  };

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {isEditing ? 'Edit Calibration Procedure' : 'Create Calibration Procedure'}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Name */}
          <div className="space-y-2">
            <Label htmlFor="proc-name">Name *</Label>
            <Input
              id="proc-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              placeholder="Procedure name"
            />
          </div>

          {/* Due Date (months) */}
          <div className="space-y-2">
            <Label htmlFor="proc-due-date">Due Date (months)</Label>
            <Input
              id="proc-due-date"
              type="number"
              min={1}
              value={dueDateMonths}
              onChange={(e) => setDueDateMonths(Number(e.target.value))}
              placeholder="12"
            />
          </div>

          {/* Calibrators multi-select */}
          <div className="space-y-2">
            <Label>Calibrators</Label>
            <div className="border rounded-md max-h-40 overflow-y-auto p-2 space-y-2">
              {calibrators.length === 0 && (
                <p className="text-sm text-muted-foreground">No calibrators available.</p>
              )}
              {calibrators.map((cal) => (
                <div key={cal.id} className="flex items-center gap-2">
                  <Checkbox
                    id={`cal-${cal.id}`}
                    checked={selectedCalibratorIds.includes(cal.mongoCalibratorId)}
                    onCheckedChange={(checked) =>
                      handleCalibratorToggle(cal.mongoCalibratorId, !!checked)
                    }
                    disabled={isPending}
                  />
                  <label
                    htmlFor={`cal-${cal.id}`}
                    className="text-sm cursor-pointer"
                  >
                    {cal.name}
                    {cal.serialNumber && (
                      <span className="text-muted-foreground ml-1">
                        ({cal.serialNumber})
                      </span>
                    )}
                  </label>
                </div>
              ))}
            </div>
          </div>

          {/* Tags */}
          <div className="space-y-2">
            <Label>Tags</Label>
            <div className="flex flex-wrap gap-4">
              {PROCEDURE_TAGS.map((tag) => (
                <div key={tag} className="flex items-center gap-2">
                  <Checkbox
                    id={`tag-${tag}`}
                    checked={tags.includes(tag)}
                    onCheckedChange={(checked) => handleTagToggle(tag, !!checked)}
                    disabled={isPending}
                  />
                  <label htmlFor={`tag-${tag}`} className="text-sm cursor-pointer">
                    {tag}
                  </label>
                </div>
              ))}
            </div>
          </div>

          {/* Measurement Templates */}
          <MeasurementTemplateEditor
            value={measurementTemplates}
            onChange={setMeasurementTemplates}
            disabled={isPending}
          />

          {/* Description */}
          <div className="space-y-2">
            <Label htmlFor="proc-description">Description</Label>
            <Textarea
              id="proc-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Procedure description..."
              rows={3}
            />
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isPending}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isPending}>
              {isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {isEditing ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
